import { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Modal, Spinner, Table } from 'react-bootstrap';
import { apiGet, apiPost } from '../services/apiClient';

const statusVariant = {
  mapped: 'success',
  unmapped: 'secondary',
  sync_error: 'danger',
};

const jobVariant = {
  success: 'success',
  running: 'warning',
  queued: 'secondary',
  failed: 'danger',
};

const ChannelSyncMonitorPage = ({ token }) => {
  const [mappings, setMappings] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedJob, setSelectedJob] = useState(null);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [mappingRes, jobRes] = await Promise.all([
        apiGet('/api/admin/channel/mappings', token),
        apiGet('/api/admin/channel/jobs', token),
      ]);
      setMappings(mappingRes || []);
      setJobs(jobRes || []);
    } catch (err) {
      setError(err.message || 'Failed to load sync data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSync = async (id) => {
    await apiPost(`/api/admin/channel/mappings/${id}/sync`, {}, token);
    await load();
  };

  const recentJobs = useMemo(() => jobs.slice(0, 50), [jobs]);

  return (
    <div className="p-4 bg-white rounded shadow-sm">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h4 className="mb-0">API Management - Sync Monitor</h4>
          <small className="text-muted">Track sync health and run manual syncs.</small>
        </div>
        <Button variant="outline-secondary" onClick={load}>
          Refresh
        </Button>
      </div>

      {loading && (
        <div className="py-4 text-center">
          <Spinner animation="border" />
        </div>
      )}
      {error && <Alert variant="danger">{error}</Alert>}

      {!loading && (
        <>
          <h6 className="mb-2">Mappings</h6>
          <Table bordered hover responsive className="align-middle mb-4">
            <thead className="table-light">
              <tr>
                <th>Property</th>
                <th>Provider</th>
                <th>Status</th>
                <th>Last sync</th>
                <th>Frequency</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {mappings.map((mapping) => (
                <tr key={mapping.id}>
                  <td>
                    <div className="fw-semibold">{mapping.property_title}</div>
                    <small className="text-muted">{mapping.property_slug}</small>
                  </td>
                  <td>
                    <div className="fw-semibold">{mapping.provider_name}</div>
                    <small className="text-muted">{mapping.connection_label}</small>
                  </td>
                  <td>
                    <Badge bg={statusVariant[mapping.mapping_status] || 'secondary'}>
                      {mapping.mapping_status}
                    </Badge>
                  </td>
                  <td>{mapping.last_sync_at || '—'}</td>
                  <td>{mapping.sync_frequency_minutes} min</td>
                  <td>
                    <Button size="sm" variant="outline-success" onClick={() => handleSync(mapping.id)}>
                      Sync now
                    </Button>
                  </td>
                </tr>
              ))}
              {!mappings.length && (
                <tr>
                  <td colSpan={6} className="text-center text-muted">
                    No mappings configured.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>

          <h6 className="mb-2">Recent jobs</h6>
          <Table bordered hover responsive className="align-middle">
            <thead className="table-light">
              <tr>
                <th>Job</th>
                <th>Status</th>
                <th>Created</th>
                <th>Property</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {recentJobs.map((job) => (
                <tr key={job.id}>
                  <td>{job.job_type}</td>
                  <td>
                    <Badge bg={jobVariant[job.status] || 'secondary'}>{job.status}</Badge>
                  </td>
                  <td>{job.created_at}</td>
                  <td>{job.property_title || '—'}</td>
                  <td>
                    <Button size="sm" variant="outline-primary" onClick={() => setSelectedJob(job)}>
                      View payload
                    </Button>
                  </td>
                </tr>
              ))}
              {!recentJobs.length && (
                <tr>
                  <td colSpan={5} className="text-center text-muted">
                    No jobs yet.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </>
      )}

      <Modal show={!!selectedJob} onHide={() => setSelectedJob(null)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Job payload</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <div className="fw-semibold">Request</div>
            <pre className="bg-light p-3 rounded small">
              {JSON.stringify(selectedJob?.request_payload || {}, null, 2)}
            </pre>
          </div>
          <div>
            <div className="fw-semibold">Response</div>
            <pre className="bg-light p-3 rounded small">
              {JSON.stringify(selectedJob?.response_payload || {}, null, 2)}
            </pre>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setSelectedJob(null)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ChannelSyncMonitorPage;
